import React, { useEffect, useState } from "react";
import { monthLabel } from "@/lib/onboardingRollover";

import axios from "axios";
import { API, formatGBP, formatMonthLong } from "@/lib/brand";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowClockwise, Plus, Trash, FloppyDisk, DownloadSimple, FilePdf, EnvelopeSimple, CaretDown, CaretRight, Export, Lock } from "@phosphor-icons/react";
import MoneyInput from "@/components/MoneyInput";
import ForecastStoryCard from "@/components/ForecastStoryCard";
import InfoTooltip from "@/components/InfoTooltip";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Plans that unlock CSV export. Kept in sync with backend FULL_FEATURE_PLANS.
const CSV_UNLOCKED_PLANS = new Set(["subscription", "annual", "one_off"]);

export default function ForecastPage() {
  const { user } = useAuth();
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(null);
  const [perMonthPay, setPerMonthPay] = useState(false);
  const canExportCsv = CSV_UNLOCKED_PLANS.has(user?.plan);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/forecast`);
      setForecast(res.data);
      if (res.data) {
        setEdit({ ...res.data.inputs });
        setPerMonthPay(
          Array.isArray(res.data.inputs.personal_draw_overrides) &&
            res.data.inputs.personal_draw_overrides.length === 12,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Scroll to hash anchor (e.g. #forecast-story) once the page finishes rendering.
  useEffect(() => {
    if (loading || !forecast) return;
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    if (!hash) return;
    // Give ForecastStoryCard time to fetch + render its first paragraph
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
    return () => clearTimeout(t);
  }, [loading, forecast]);

  if (loading) return <div className="font-heading uppercase text-2xl">Loading…</div>;
  if (!forecast) {
    return (
      <div className="card max-w-2xl">
        <h1 className="font-heading uppercase text-3xl">No forecast yet</h1>
        <p className="mt-3 text-black/75">Let's build it together.</p>
        <Link to="/onboarding" className="btn-primary mt-6 inline-flex">Start building your forecast</Link>
      </div>
    );
  }

  const months = forecast.months;

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/forecast/inputs`, { inputs: edit });
      await axios.post(`${API}/forecast/reproject`);
      toast.success("Forecast updated.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const reproject = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/forecast/reproject`);
      toast.success("Rolling forecast refreshed.");
      await load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="section-eyebrow">Forecast</div>
          <h1 className="font-heading uppercase text-4xl md:text-5xl tracking-wide mt-4 leading-[1.05]">
            Your 12 months, <span className="highlight-yellow inline-block">on one screen.</span>
          </h1>
          <p className="mt-3 text-black/70 leading-relaxed">
            Scroll across to see every month. Yellow cells flag when your cash dips below zero —
            your early warning sign. When you spot a dip months ahead, you can act now and change
            the outcome. That's the power of forecasting: influence the future instead of fearing
            it.
          </p>
          {months?.length > 0 && forecast.inputs?.start_month && (
            <p className="mt-2 text-sm font-semibold" data-testid="forecast-range">
              Your forecast covers {monthLabel(forecast.inputs.start_month, 0)} – {monthLabel(forecast.inputs.start_month, 11)}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-secondary" onClick={reproject} data-testid="reproject-btn">
            <ArrowClockwise size={16} weight="bold" /> Refresh rolling forecast
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="btn-secondary" data-testid="export-menu-btn">
                <Export size={16} weight="bold" /> Export <CaretDown size={14} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 bg-white border-2 border-black shadow-[4px_4px_0_0_#000] rounded-xl p-1 z-50">
              <DropdownMenuLabel>Take your forecast with you</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer gap-2"
                data-testid="export-csv-item"
                onClick={async () => {
                  if (!canExportCsv) {
                    toast.message(
                      "CSV export is part of the paid subscription.",
                      { description: "Your branded PDF export is available on every plan." }
                    );
                    return;
                  }
                  try {
                    const res = await axios.get(`${API}/forecast/csv`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `forecastly_export_${new Date().toISOString().slice(0, 10)}.csv`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch {
                    toast.error("Could not download CSV");
                  }
                }}
              >
                {canExportCsv ? (
                  <DownloadSimple size={16} weight="bold" />
                ) : (
                  <Lock size={16} weight="bold" data-testid="csv-lock-icon" />
                )}
                <div className="flex flex-col">
                  <span className="font-semibold">
                    Download CSV{!canExportCsv && " · Paid feature"}
                  </span>
                  <span className="text-xs text-black/60">
                    {canExportCsv
                      ? "Forecast spreadsheet-ready"
                      : "Unlocks after your 10-day trial converts"}
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer gap-2"
                data-testid="export-pdf-item"
                onClick={async () => {
                  const id = toast.loading("Generating your branded PDF…");
                  try {
                    const res = await axios.get(`${API}/forecast/pdf`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `forecastly_${new Date().toISOString().slice(0, 10)}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Your PDF is ready.", { id });
                  } catch {
                    toast.error("Could not download PDF — please try again.", { id });
                  }
                }}
              >
                <FilePdf size={16} weight="bold" />
                <div className="flex flex-col">
                  <span className="font-semibold">Download PDF</span>
                  <span className="text-xs text-black/60">Branded, ready to share</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer gap-2"
                data-testid="export-email-item"
                onClick={async () => {
                  const id = toast.loading("Generating & emailing your PDF…");
                  try {
                    const res = await axios.post(`${API}/forecast/pdf/email`);
                    if (res.data?.ok) {
                      toast.success(`Sent to ${res.data.to}. Check your inbox.`, { id });
                    } else {
                      toast.error("Email send failed.", { id });
                    }
                  } catch (e) {
                    toast.error(
                      e?.response?.data?.detail || "Email send failed — please try again.",
                      { id }
                    );
                  }
                }}
              >
                <EnvelopeSimple size={16} weight="bold" />
                <div className="flex flex-col">
                  <span className="font-semibold">Email me the PDF</span>
                  <span className="text-xs text-black/60">Land it in your inbox</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="forecast-wrapper" data-testid="forecast-table">
        <table className="forecast-table">
          <thead>
            <tr>
              <th className="first">Month</th>
              {months.map((m) => (
                <th key={m.index}>{m.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Income rows per stream */}
            {forecast.inputs.revenue_streams.map((rs) => (
              <tr key={`r-${rs.id}`}>
                <td className="first">{rs.name}</td>
                {months.map((m) => {
                  const line = (m.income_lines || []).find((l) => l.id === rs.id);
                  return <td key={m.index}>{formatGBP(line?.amount || 0)}</td>;
                })}
              </tr>
            ))}
            <tr className="total-row">
              <td className="first">Total Money In</td>
              {months.map((m) => (
                <td key={m.index}>{formatGBP(m.total_income)}</td>
              ))}
            </tr>
            {/* Cost rows per recurring — use m.cost_lines so frequency / months_active are honoured */}
            {forecast.inputs.recurring_costs.map((c) => (
              <tr key={`c-${c.id}`}>
                <td className="first">{c.name}</td>
                {months.map((m) => {
                  const line = (m.cost_lines || []).find((l) => l.id === c.id);
                  return <td key={m.index}>{line ? formatGBP(-line.amount) : "—"}</td>;
                })}
              </tr>
            ))}
            {forecast.inputs.one_off_costs.map((c) => (
              <tr key={`o-${c.id}`}>
                <td className="first">{c.name} (one-off)</td>
                {months.map((m) => (
                  <td key={m.index}>{c.month_index === m.index ? formatGBP(-c.amount) : "—"}</td>
                ))}
              </tr>
            ))}
            <tr>
              <td className="first">Personal pay</td>
              {months.map((m) => (
                <td key={m.index}>{formatGBP(-m.personal_draw)}</td>
              ))}
            </tr>
            <tr className="total-row">
              <td className="first">Total Money Out</td>
              {months.map((m) => (
                <td key={m.index}>{formatGBP(-(m.total_costs + m.personal_draw))}</td>
              ))}
            </tr>
            <tr className="total-row">
              <td className="first">Net cash flow</td>
              {months.map((m) => (
                <td
                  key={m.index}
                  className={m.net < 0 ? "cell-warn" : ""}
                  data-testid={`net-cell-${m.index}`}
                >
                  {formatGBP(m.net)}
                </td>
              ))}
            </tr>
            <tr className="total-row">
              <td className="first">Cash balance</td>
              {months.map((m) => (
                <td key={m.index} className={m.cash_balance < 0 ? "cell-warn" : ""}>
                  {formatGBP(m.cash_balance)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Editor */}
      <div className="card">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h2 className="font-heading uppercase text-2xl">Fine-tune your inputs</h2>
            <p className="text-black/70 text-sm mt-1">Changes regenerate your full 12 months.</p>
          </div>
          <button className="btn-primary" onClick={save} disabled={saving} data-testid="save-inputs-btn">
            <FloppyDisk size={18} weight="bold" /> {saving ? "Saving…" : "Save & recompute"}
          </button>
        </div>

        <div className="mt-6 space-y-4">
        <CollapsibleSection title="Cash setup" testid="section-cash-setup" defaultOpen={false}>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-1">
                Opening cash (£) — at the start of{" "}
                {formatMonthLong(edit.start_month) || "your forecast"}
              </label>
              <MoneyInput
                value={edit.opening_cash}
                onChange={(n) => setEdit({ ...edit, opening_cash: n })}
                testid="edit-opening-cash"
                allowNegative
              />
              <p className="text-xs text-black/55 mt-2 leading-relaxed">
                Keep this up to date so your cash-balance line stays honest — especially if you
                started your forecast in a future month.
              </p>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Start month</label>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-heading uppercase text-2xl" data-testid="edit-start-month-display">
                  {formatMonthLong(edit.start_month) || "—"}
                </span>
                <span className="chip">Locked</span>
              </div>
              <p className="text-xs text-black/55 mt-2 leading-relaxed">
                Every forecast is anchored to the month it was created. To reset the start month,
                use <strong>Start again</strong>.
              </p>
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Offers" testid="section-offers" defaultOpen={false}>
          <OffersEditor
            items={edit.revenue_streams}
            onChange={(items) => setEdit({ ...edit, revenue_streams: items })}
            monthLabels={forecast.months.map((m) => m.label.split(" ")[0])}
            startMonthIso={edit.start_month}
          />
        </CollapsibleSection>

        <CollapsibleSection title="Monthly costs" testid="section-monthly-costs" defaultOpen={false}>
          <RecurringCostsEditor
            items={edit.recurring_costs}
            onChange={(items) => setEdit({ ...edit, recurring_costs: items })}
            startMonthIso={edit.start_month}
          />
        </CollapsibleSection>

        <CollapsibleSection title="One-off or annual costs" testid="section-oneoff-costs" defaultOpen={false}>
          <OneOffCostsEditor
            items={edit.one_off_costs}
            onChange={(items) => setEdit({ ...edit, one_off_costs: items })}
            startMonthIso={edit.start_month}
          />
        </CollapsibleSection>

        <CollapsibleSection title="Personal pay" testid="section-personal-pay" defaultOpen={false}>
          <div>
            <label className="block text-sm font-semibold mb-1">Monthly personal pay (£)</label>
            <div className="max-w-xs">
              <MoneyInput
                value={edit.personal_draw}
                onChange={(n) => setEdit({ ...edit, personal_draw: n })}
                testid="edit-personal-draw"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-xs mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={perMonthPay}
                onChange={(e) => {
                  const on = e.target.checked;
                  setPerMonthPay(on);
                  if (on) {
                    const seed =
                      Array.isArray(edit.personal_draw_overrides) &&
                      edit.personal_draw_overrides.length === 12
                        ? edit.personal_draw_overrides
                        : Array(12).fill(Number(edit.personal_draw) || 0);
                    setEdit({ ...edit, personal_draw_overrides: seed });
                  } else {
                    setEdit({ ...edit, personal_draw_overrides: null });
                  }
                }}
                data-testid="edit-permonth-toggle"
              />
              Set per-month (e.g. holidays, post-launch payrise)
            </label>
          </div>

          {perMonthPay && (
            <div className="mt-6" data-testid="permonth-pay-grid">
              <h4 className="font-heading uppercase text-base">Personal pay · per month</h4>
              <p className="text-xs text-black/55 mt-1">
                Overrides the flat rate above. Use 0 for months you won't pay yourself (e.g.
                holidays or a quiet season).
              </p>
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {forecast.months.map((m, i) => (
                  <div key={i}>
                    <label className="block text-[11px] font-semibold mb-1 uppercase tracking-wide">
                      {m.label.split(" ")[0]}
                    </label>
                    <MoneyInput
                      value={
                        Array.isArray(edit.personal_draw_overrides)
                          ? edit.personal_draw_overrides[i] ?? 0
                          : 0
                      }
                      onChange={(n) => {
                        const arr = Array.isArray(edit.personal_draw_overrides)
                          ? [...edit.personal_draw_overrides]
                          : Array(12).fill(Number(edit.personal_draw) || 0);
                        arr[i] = n;
                        setEdit({ ...edit, personal_draw_overrides: arr });
                      }}
                      testid={`permonth-pay-${i}`}
                      prefix={null}
                      className="input !py-2"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </CollapsibleSection>

        <div
          className="mt-6 ai-insight flex items-start gap-2"
          data-testid="recompute-reminder"
        >
          <span>
            <strong>Heads up:</strong> changes only land in your forecast once you tap{" "}
            <strong>Save & recompute</strong> at the top of this card.
          </span>
        </div>
      </div>
      </div>

      {/* AI-generated annual narrative — lives under Fine-tune so users see it
          AFTER they've absorbed the numbers and tweaked their inputs. */}
      <div id="forecast-story" className="scroll-mt-24">
        <ForecastStoryCard />
      </div>
    </div>
  );
}

function CollapsibleSection({ title, testid, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-[1.5px] border-black/15 rounded-xl" data-testid={testid}>
      <button
        type="button"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-black/[0.02]"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-toggle`}
      >
        <span className="font-heading uppercase text-lg">{title}</span>
        <span className="flex items-center gap-2 text-sm">
          <span className="text-black/55">{open ? "Close" : "Edit"}</span>
          {open ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
        </span>
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-black/10">{children}</div>}
    </div>
  );
}

const FORECAST_FREQ_OPTIONS = [
  { v: "monthly", label: "Every month" },
  { v: "quarterly", label: "Quarterly" },
  { v: "bi_annual", label: "Bi-annually" },
  { v: "annual", label: "Once a year" },
  { v: "custom", label: "Custom months" },
];

function buildLongMonthLabels(startMonthIso) {
  const longMonths = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  if (!startMonthIso) return longMonths;
  const [y, m] = startMonthIso.split("-").map(Number);
  if (!y || !m) return longMonths;
  const out = [];
  for (let i = 0; i < 12; i++) {
    const t = m - 1 + i;
    const yOut = y + Math.floor(t / 12);
    out.push(`${longMonths[t % 12]} ${yOut}`);
  }
  return out;
}

function RecurringCostsEditor({ items, onChange, startMonthIso }) {
  const longLabels = buildLongMonthLabels(startMonthIso);
  const shortLabels = longLabels.map((l) => l.split(" ")[0]);

  const update = (i, patch) =>
    onChange(items.map((it, k) => (k === i ? { ...it, ...patch } : it)));
  const remove = (i) => onChange(items.filter((_, k) => k !== i));
  const add = () =>
    onChange([
      ...items,
      {
        id: `c${Date.now()}`,
        name: "",
        amount: 0,
        kind: "monthly",
        frequency: "monthly",
        anchor_month_index: 0,
        months_active: null,
      },
    ]);

  return (
    <div className="mt-2 space-y-3">
      {items.map((c, i) => {
        const freq = c.frequency || "monthly";
        return (
          <div key={c.id || i} className="border-[1.5px] border-black/15 rounded-lg p-3" data-testid={`recurring-row-${i}`}>
            <div className="grid md:grid-cols-[1fr_140px_180px_auto] gap-3 items-end">
              <div>
                <label className="block text-xs font-semibold mb-1">Name</label>
                <input
                  className="input"
                  value={c.name ?? ""}
                  onChange={(e) => update(i, { name: e.target.value })}
                  data-testid={`recurring-name-${i}`}
                  placeholder="e.g. Software subs"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Amount £</label>
                <MoneyInput
                  value={c.amount ?? 0}
                  onChange={(n) => update(i, { amount: n })}
                  testid={`recurring-amount-${i}`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">How often?</label>
                <select
                  className="input"
                  value={freq}
                  onChange={(e) => update(i, { frequency: e.target.value, months_active: null })}
                  data-testid={`recurring-freq-${i}`}
                >
                  {FORECAST_FREQ_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="btn-secondary !px-3 !py-2"
                onClick={() => remove(i)}
                data-testid={`recurring-remove-${i}`}
              >
                <Trash size={16} />
              </button>
            </div>
            {freq !== "monthly" && freq !== "custom" && (
              <div className="mt-3 max-w-md">
                <label className="block text-xs font-semibold mb-1">First time it lands</label>
                <select
                  className="input"
                  value={c.anchor_month_index ?? 0}
                  onChange={(e) => update(i, { anchor_month_index: Number(e.target.value) })}
                  data-testid={`recurring-anchor-${i}`}
                >
                  {longLabels.map((label, k) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {freq === "custom" && (
              <div className="mt-3" data-testid={`recurring-custom-grid-${i}`}>
                <label className="block text-xs font-semibold mb-2">
                  Tick the months this cost actually leaves your account
                </label>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-2">
                  {shortLabels.map((m, k) => {
                    const on = (c.months_active || []).includes(k);
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => {
                          const set = new Set(c.months_active || []);
                          if (set.has(k)) set.delete(k);
                          else set.add(k);
                          update(i, { months_active: Array.from(set).sort((a, b) => a - b) });
                        }}
                        data-testid={`recurring-month-${i}-${k}`}
                        className={`px-2 py-2 rounded-lg border-[1.5px] border-black text-sm font-heading uppercase tracking-wide ${
                          on ? "bg-[#FFBD4A]" : "bg-white"
                        }`}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
      <button className="btn-secondary" onClick={add} data-testid="recurring-add">
        <Plus size={16} weight="bold" /> Add monthly cost
      </button>
    </div>
  );
}

function OneOffCostsEditor({ items, onChange, startMonthIso }) {
  const longLabels = buildLongMonthLabels(startMonthIso);
  const update = (i, patch) =>
    onChange(items.map((it, k) => (k === i ? { ...it, ...patch } : it)));
  const remove = (i) => onChange(items.filter((_, k) => k !== i));
  const add = () =>
    onChange([
      ...items,
      { id: `o${Date.now()}`, name: "", amount: 0, kind: "one_off", month_index: 0 },
    ]);

  return (
    <div className="mt-2 space-y-3">
      {items.map((c, i) => (
        <div
          key={c.id || i}
          className="grid md:grid-cols-[1fr_140px_220px_auto] gap-3 items-end border-[1.5px] border-black/15 rounded-lg p-3"
          data-testid={`oneoff-row-${i}`}
        >
          <div>
            <label className="block text-xs font-semibold mb-1">Name</label>
            <input
              className="input"
              value={c.name ?? ""}
              onChange={(e) => update(i, { name: e.target.value })}
              data-testid={`oneoff-name-${i}`}
              placeholder="e.g. Annual insurance"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Amount £</label>
            <MoneyInput
              value={c.amount ?? 0}
              onChange={(n) => update(i, { amount: n })}
              testid={`oneoff-amount-${i}`}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">In which month?</label>
            <select
              className="input"
              value={Number(c.month_index) || 0}
              onChange={(e) => update(i, { month_index: Number(e.target.value) })}
              data-testid={`oneoff-month-${i}`}
            >
              {longLabels.map((label, k) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <button
            className="btn-secondary !px-3 !py-2"
            onClick={() => remove(i)}
            data-testid={`oneoff-remove-${i}`}
          >
            <Trash size={16} />
          </button>
        </div>
      ))}
      <button className="btn-secondary" onClick={add} data-testid="oneoff-add">
        <Plus size={16} weight="bold" /> Add one-off cost
      </button>
    </div>
  );
}


const SEASONALITY_PRESETS = {
  steady: Array(12).fill(1),
  summer_peak: [0.7, 0.7, 0.85, 1, 1.15, 1.3, 1.4, 1.3, 1.1, 0.9, 0.8, 0.7],
  winter_peak: [1.3, 1.1, 0.9, 0.8, 0.75, 0.7, 0.7, 0.8, 0.95, 1.1, 1.3, 1.5],
  q1_slow: [0.6, 0.7, 0.85, 1, 1.1, 1.15, 1.15, 1.15, 1.1, 1.05, 1.1, 1.05],
};

const SEASONALITY_PRESET_LABELS = [
  { v: "steady", label: "Steady (no swing)" },
  { v: "summer_peak", label: "Summer peak" },
  { v: "winter_peak", label: "Winter peak" },
  { v: "q1_slow", label: "Q1 slow start" },
  { v: "custom", label: "Custom (12 multipliers)" },
];

// 12 short month names anchored to the calendar Jan-Dec — used for the seasonality grid
// since seasonality is keyed to the calendar, not the start month.
const CAL_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function detectPreset(seasonality) {
  if (!Array.isArray(seasonality) || seasonality.length !== 12) return "steady";
  for (const [k, arr] of Object.entries(SEASONALITY_PRESETS)) {
    if (arr.every((v, i) => Math.abs(v - seasonality[i]) < 0.001)) return k;
  }
  return "custom";
}

function OffersEditor({ items, onChange, monthLabels, startMonthIso }) {
  const update = (i, patch) =>
    onChange(items.map((it, k) => (k === i ? { ...it, ...patch } : it)));
  const remove = (i) => onChange(items.filter((_, k) => k !== i));
  const add = () =>
    onChange([
      ...items,
      {
        id: `rs${Date.now()}`,
        name: "",
        price: 0,
        clients_per_month: 0,
        offer_type: "service",
        seasonality: null,
        monthly_sales: null,
      },
    ]);

  return (
    <div className="mt-8">
      <h3 className="font-heading uppercase text-xl">Offers</h3>
      <p className="text-xs text-black/55 mt-1">
        Edit prices, sales-per-month, and seasonality for each offer. Changes regenerate your full
        12 months.
      </p>
      <div className="mt-4 space-y-3">
        {items.map((r, i) => (
          <OfferEditCard
            key={r.id || i}
            r={r}
            i={i}
            monthLabels={monthLabels}
            startMonthIso={startMonthIso}
            onPatch={(p) => update(i, p)}
            onRemove={() => remove(i)}
          />
        ))}
        <button className="btn-secondary" onClick={add} data-testid="offer-add">
          <Plus size={16} weight="bold" /> Add offer
        </button>
      </div>
    </div>
  );
}

function OfferEditCard({ r, i, monthLabels, startMonthIso, onPatch, onRemove }) {
  const [open, setOpen] = useState(false);
  const customSales = Array.isArray(r.monthly_sales) && r.monthly_sales.length === 12;
  const currentPreset = detectPreset(r.seasonality);

  const setCustomSales = (on) => {
    if (on) {
      const seed = Array.from({ length: 12 }, () => Number(r.clients_per_month) || 0);
      onPatch({ monthly_sales: seed });
    } else {
      onPatch({ monthly_sales: null });
    }
  };
  const updateMonthSales = (k, v) => {
    const arr = Array.isArray(r.monthly_sales) ? [...r.monthly_sales] : Array(12).fill(0);
    arr[k] = Number(v) || 0;
    onPatch({ monthly_sales: arr });
  };

  const setPreset = (preset) => {
    if (preset === "custom") {
      const seed = Array.isArray(r.seasonality) && r.seasonality.length === 12
        ? r.seasonality
        : Array(12).fill(1);
      onPatch({ seasonality: seed });
      return;
    }
    if (preset === "steady") {
      onPatch({ seasonality: null });
      return;
    }
    onPatch({ seasonality: SEASONALITY_PRESETS[preset] });
  };
  const updateSeasMult = (k, v) => {
    const arr = Array.isArray(r.seasonality) && r.seasonality.length === 12
      ? [...r.seasonality]
      : Array(12).fill(1);
    arr[k] = Number(v) || 0;
    onPatch({ seasonality: arr });
  };

  // Seasonality grid is calendar-anchored: show all 12 calendar months (Jan-Dec)
  // with a yellow ring on the user's start month for context.
  const startCal = startMonthIso ? Number(startMonthIso.split("-")[1]) - 1 : 0;

  return (
    <div className="card !p-4" data-testid={`offer-row-${i}`}>
      <div className="grid md:grid-cols-[1fr_120px_140px_auto_auto] gap-3 items-end">
        <div>
          <label className="block text-xs font-semibold mb-1">Name</label>
          <input
            className="input"
            value={r.name ?? ""}
            onChange={(e) => onPatch({ name: e.target.value })}
            data-testid={`offer-name-${i}`}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">Price £</label>
          <input
            type="number"
            className="input"
            value={r.price ?? 0}
            onChange={(e) => onPatch({ price: Number(e.target.value) || 0 })}
            data-testid={`offer-price-${i}`}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">Clients / mo</label>
          <input
            type="number"
            className="input"
            value={r.clients_per_month ?? 0}
            onChange={(e) => onPatch({ clients_per_month: Number(e.target.value) || 0 })}
            disabled={customSales}
            data-testid={`offer-clients_per_month-${i}`}
          />
        </div>
        <button
          type="button"
          className="btn-secondary !px-3 !py-2"
          onClick={() => setOpen((o) => !o)}
          data-testid={`offer-toggle-${i}`}
          title={open ? "Hide advanced" : "Show advanced (sales per month / seasonality)"}
        >
          {open ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
          <span className="hidden sm:inline">{open ? "Less" : "More"}</span>
        </button>
        <button
          className="btn-secondary !px-3 !py-2"
          onClick={onRemove}
          data-testid={`offer-remove-${i}`}
        >
          <Trash size={16} />
        </button>
      </div>

      {open && (
        <div className="mt-5 border-t border-black/10 pt-4 space-y-5" data-testid={`offer-advanced-${i}`}>
          <div>
            <label className="block text-xs font-semibold mb-2">Offer type</label>
            <div className="flex flex-wrap gap-2">
              {[
                { v: "service", label: "Service / 1:1" },
                { v: "product", label: "Product" },
                { v: "one_off_project", label: "One-off project" },
                { v: "other", label: "Other" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => onPatch({ offer_type: opt.v })}
                  data-testid={`offer-type-${i}-${opt.v}`}
                  className={`px-3 py-1.5 rounded-full border-[1.5px] border-black text-xs font-heading uppercase tracking-wide ${
                    (r.offer_type || "service") === opt.v ? "bg-[#FFBD4A]" : "bg-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {Array.isArray(r.monthly_amounts) && (
            <div className="ai-insight text-sm" data-testid={`offer-custom-amounts-note-${i}`}>
              This income stream uses custom monthly amounts from your setup.{" "}
              <button type="button" className="underline font-semibold" onClick={() => onPatch({ monthly_amounts: null })} data-testid={`offer-clear-amounts-${i}`}>
                Switch back to one regular amount
              </button>
            </div>
          )}

          {/* Per-month sales */}
          <div>
            <label className="inline-flex items-start gap-2 text-sm font-semibold cursor-pointer">
              <input
                type="checkbox"
                checked={customSales}
                onChange={(e) => setCustomSales(e.target.checked)}
                className="w-4 h-4 mt-0.5"
                data-testid={`offer-customsales-toggle-${i}`}
              />
              <span>
                Customise sales per month — if the month for your future launch has changed, simply
                add a <strong>0</strong> for the original launch month and a <strong>1</strong> or
                more for the months you're starting to sell, plus how many you'll sell each month
                thereafter.
              </span>
            </label>
            {customSales && (
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid={`offer-monthly-grid-${i}`}>
                {monthLabels.map((m, k) => (
                  <div key={k}>
                    <label className="block text-[11px] font-semibold mb-1 uppercase tracking-wide">
                      {m}
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="input !py-2"
                      value={r.monthly_sales?.[k] ?? 0}
                      onChange={(e) => updateMonthSales(k, e.target.value)}
                      data-testid={`offer-monthly-${i}-${k}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Seasonality */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold mb-2">
              <span>Seasonality {customSales ? "(disabled while customising sales per month)" : ""}</span>
              <InfoTooltip label="Seasonality">
                Seasonality is the rhythm of your year — some months naturally sell more than others (busy summer, quiet January).
                Multipliers are calendar-anchored: 1.0 = your baseline sales; 1.5 = 50% busier; 0.5 = half as busy.
                Pick a preset or type your own numbers for each month.
              </InfoTooltip>
            </label>
            <select
              className="input max-w-sm"
              value={currentPreset}
              onChange={(e) => setPreset(e.target.value)}
              disabled={customSales}
              data-testid={`offer-seasonality-preset-${i}`}
            >
              {SEASONALITY_PRESET_LABELS.map((o) => (
                <option key={o.v} value={o.v}>
                  {o.label}
                </option>
              ))}
            </select>
            {!customSales && currentPreset !== "steady" && (
              <div className="mt-3" data-testid={`offer-seasonality-grid-${i}`}>
                <p className="text-xs text-black/55 mb-2">
                  Multipliers are calendar-anchored (Jan–Dec). 1.0 = baseline. Your start month is
                  highlighted.
                </p>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-2">
                  {CAL_MONTHS.map((m, k) => (
                    <div key={k} className={k === startCal ? "ring-2 ring-[#FFBD4A] rounded-lg p-1" : "p-1"}>
                      <label className="block text-[10px] font-semibold mb-1 uppercase tracking-wide">
                        {m}
                      </label>
                      <input
                        type="number"
                        step="0.05"
                        min="0"
                        className="input !py-1.5 !text-sm"
                        value={r.seasonality?.[k] ?? 1}
                        onChange={(e) => updateSeasMult(k, e.target.value)}
                        data-testid={`offer-seasonality-${i}-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
